import type { NextStepTarget } from '@/api/pipeline';
import { recordPath } from '@/ui';

export type DealTabId =
  'overview' | 'consultation' | 'proposal' | 'decision' | 'installation' | 'follow-up' | 'history';

export interface DealTab {
  readonly id: DealTabId;
  readonly label: string;
  /** The path beneath the deal; the overview is the deal's own path. */
  readonly segment: string;
}

/** The tabs of a deal, in the order a deal moves through them. */
export const DEAL_TABS: readonly DealTab[] = [
  { id: 'overview', label: 'Overview', segment: '' },
  { id: 'consultation', label: 'Consultation', segment: 'consultation' },
  { id: 'proposal', label: 'Proposal', segment: 'proposal' },
  { id: 'decision', label: 'Decision', segment: 'decision' },
  { id: 'installation', label: 'Installation', segment: 'installation' },
  { id: 'follow-up', label: 'Follow-up', segment: 'follow-up' },
  { id: 'history', label: 'History', segment: 'history' },
];

// Typed on the server's targets, so a target the server adds is a compile error until it has a tab.
const TAB_OF_TARGET: Record<NextStepTarget, DealTabId> = {
  OVERVIEW: 'overview',
  CONSULTATION: 'consultation',
  PROPOSAL: 'proposal',
  DECISION: 'decision',
  INSTALLATION: 'installation',
  FOLLOW_UP: 'follow-up',
};

/**
 * Where a tab of a deal lives.
 *
 * @param dealId the deal
 * @param tab which tab
 * @returns the app path
 */
export function dealTabPath(dealId: string, tab: DealTabId): string {
  const segment = DEAL_TABS.find((candidate) => candidate.id === tab)?.segment ?? '';
  const base = recordPath('deal', dealId);
  return segment === '' ? base : `${base}/${segment}`;
}

/**
 * The tab a next step is done on.
 *
 * @param target what the server named
 * @returns the tab
 */
export function tabOfTarget(target: NextStepTarget): DealTabId {
  return TAB_OF_TARGET[target];
}

/**
 * The tab a path beneath a deal belongs to; anything unknown is the overview.
 *
 * @param segment the first path segment after the deal's id, or an empty string
 * @returns the tab
 */
export function tabOfSegment(segment: string): DealTabId {
  return DEAL_TABS.find((tab) => tab.segment === segment)?.id ?? 'overview';
}
